// Plans/2026-10-19-vorab-bestand-plan.md §2.4: offene Vorab-Zeilen. Der Vorab bleibt sichtbar, bis DR die
// Lieferung fuehrt (automatisch) oder der Leiter ihn mit Grund als uebernommen schliesst.
import { useEffect, useState } from 'react'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useVorabUebernehmen, type VorabOffenZeile } from '@/api/vorab'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'

const zahl = (n: number) => n.toLocaleString('de-DE')

function UebernehmenDialog({ zeile, onClose }: { zeile: VorabOffenZeile | null; onClose: () => void }) {
  const [grund, setGrund] = useState('')
  const uebernehmen = useVorabUebernehmen()
  useEffect(() => { if (zeile) setGrund('') }, [zeile])
  const gueltig = grund.trim().length >= 3

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!zeile || !gueltig) return
    try {
      await uebernehmen.mutateAsync({ id: zeile.id, grund: grund.trim() })
      toast.success(`Vorab ${zeile.merchant_sku ?? zeile.sku} übernommen`)
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Vorab übernehmen fehlgeschlagen')
    }
  }

  return (
    <Dialog open={zeile != null} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Vorab übernommen</DialogTitle>
          <DialogDescription>
            {zeile && <>Die {zahl(zeile.stueck)} Stück Vorab für <b><GrundartikelLink code={zeile.merchant_sku ?? zeile.sku} /></b> zählen danach nicht mehr
              zum Bestand. Nur schließen, wenn DR die Menge schon führt.</>}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="vorab-uebernehmen-grund">Grund (Pflichtangabe)</Label>
            <Input id="vorab-uebernehmen-grund" value={grund} onChange={(e) => setGrund(e.target.value)}
              placeholder="z. B. In DR von Hand eingetragen" autoFocus />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={!gueltig || uebernehmen.isPending}>Vorab übernommen</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function VorabOffenZeileView({ z, lead, onUebernehmen }: {
  z: VorabOffenZeile; lead: boolean; onUebernehmen: (z: VorabOffenZeile) => void
}) {
  return (
    <li className={`rounded-lg border border-line bg-background p-3 shadow-sm text-sm border-l-4 ${z.evtl_doppelt ? 'border-l-amber-500' : 'border-l-sky-500'}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <b className="text-ink"><GrundartikelLink code={z.merchant_sku ?? z.sku}>{z.merchant_sku ?? z.sku}</GrundartikelLink></b>
          {z.name && <span className="text-ink-2"> · {z.name}</span>}
          <div className="mt-1 text-xs text-ink-2">
            Bestand <span className="font-mono text-ink">{zahl(z.quantity + z.vorab_offen_kopf)}</span>
            {' '}(<span className="font-mono">{zahl(z.quantity)} DR + {zahl(z.vorab_offen_kopf)} vorab</span>)
            {z.vorab_offen_kopf !== z.stueck && <> · davon hier <span className="font-mono">{zahl(z.stueck)}</span></>}
            {' · '}<Link className="text-primary underline" to={`/warehouse/wareneingang/${z.receipt_id}`}>WE {z.receipt_id}</Link>
            {z.order_number && <> · {z.order_number}</>}
            {' · '}{new Date(z.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
            {z.created_by_name && <> · {z.created_by_name}</>}
          </div>
          {z.evtl_doppelt && (
            <div className="mt-1 text-xs font-medium text-amber-700">
              DR seit Vorab +{zahl(z.dr_plus ?? 0)} — evtl. doppelt. Prüfen, ob DR die Lieferung schon führt.
            </div>
          )}
        </div>
        {lead && <Button size="sm" variant="outline" onClick={() => onUebernehmen(z)}>Vorab übernommen…</Button>}
      </div>
    </li>
  )
}

export function VorabOffenListe({ rows, lead, leer }: { rows: VorabOffenZeile[]; lead: boolean; leer?: string }) {
  const [auswahl, setAuswahl] = useState<VorabOffenZeile | null>(null)
  return (
    <>
      <ul className="space-y-2">
        {rows.map((z) => <VorabOffenZeileView key={z.id} z={z} lead={lead} onUebernehmen={setAuswahl} />)}
        {rows.length === 0 && leer && <li className="text-ink-2 text-sm p-3">{leer}</li>}
      </ul>
      <UebernehmenDialog zeile={auswahl} onClose={() => setAuswahl(null)} />
    </>
  )
}
